import type { ToastProps } from "@nuxt/ui";

type NotificationType = "success" | "error" | "warning";

/**
 * Composable untuk menampilkan toast notification dengan tipe dan styling yang sudah dibakukan.
 *
 * Composable ini memetakan tipe notifikasi (`success`, `error`, `warning`)
 * ke ikon dan warna yang sesuai, lalu menyediakan API `add()` yang lebih ringkas
 * agar komponen pemanggil tidak perlu mengulang konfigurasi toast yang sama.
 *
 * @returns Objek utilitas toast yang berisi method `add()` dan method bawaan lain dari `useToast()`
 *
 * @example
 * ```typescript
 * const { add } = useToastNotification();
 * add("success", {
 *   title: "Berhasil",
 *   description: "Operasi berhasil dijalankan.",
 * });
 * ```
 */
export function useToastNotification() {
  const icon: Record<NotificationType, string> = {
    success: "i-lucide-circle-check",
    error: "i-lucide-circle-alert",
    warning: "i-lucide-triangle-alert",
  };

  const color: Record<NotificationType, ToastProps["color"]> = {
    success: "success",
    error: "error",
    warning: "warning",
  };

  const { add: addToast, ...otherToastMethod } = useToast();

  const add = (
    type: NotificationType,
    {
      title = "",
      description = "",
    }: {
      title?: string;
      description?: string;
    },
  ) => {
    addToast({
      title,
      description,
      icon: icon[type],
      color: color[type],
    });
  };

  return {
    add,
    ...otherToastMethod,
  };
}
